// Beacon stats + battery history (jobq-api GET /beacons/stats and GET /beacons/{id}/history,
// beacon_stats.py, 2026-10-23). Pure helpers for components/BeaconStats.jsx.
//
// JobQ started keeping battery readings on 2026-10-23 (one every 15 min while a gateway hears the
// beacon, plus one whenever charging or USB power changes). Before that only the latest reading
// existed, so every chart starts then.

import { beaconFetch } from './beacons';

export const HISTORY_RANGES = [
  { days: 1, label: '24 h' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

export const LOW_PCT = 20;
export const STATS_REFRESH_MS = 60000;

export function loadFleetStats(days = 7) {
  return beaconFetch(`/beacons/stats?days=${encodeURIComponent(days)}`);
}

export function loadBeaconHistory(beaconId, days = 7) {
  return beaconFetch(`/beacons/${encodeURIComponent(beaconId)}/history?days=${encodeURIComponent(days)}`);
}

/** Chart points: {t (ms), pct, mv, onPower, docked}. Readings with no % are dropped. */
export function chartPoints(samples) {
  return (samples || [])
    .map((s) => ({
      t: new Date(s.t).getTime(),
      pct: Number.isFinite(s.pct) ? s.pct : null,
      mv: Number.isFinite(s.mv) ? s.mv : null,
      onPower: s.charging === true || s.usb === true,
      charging: s.charging === true,
      docked: s.docked === true,
    }))
    .filter((p) => Number.isFinite(p.t) && p.pct !== null)
    .sort((a, b) => a.t - b.t);
}

/**
 * Stretches on power ({x1, x2} ms) for shaded bands: a run of on-power readings, closed at the next
 * reading off power (the beacon was on the charger until then), or at `endMs` when still on it.
 */
export function powerSpans(points, endMs = Date.now()) {
  const spans = [];
  let open = null;
  for (const p of points || []) {
    if (p.onPower && open === null) open = p.t;
    else if (!p.onPower && open !== null) {
      spans.push({ x1: open, x2: p.t });
      open = null;
    }
  }
  if (open !== null) spans.push({ x1: open, x2: Math.max(open, endMs) });
  return spans;
}

const EVENT_LABELS = {
  assigned: 'Put on a pallet',
  released: 'Released',
  auto_released: 'Released (pallet done)',
  docked: 'Plugged in',
  undocked: 'Unplugged',
  find_done: 'Find: lit',
  find_failed: 'Find failed',
  find_expired: 'Find timed out',
  find_requested: 'Find asked',
  firmware_updated: 'Firmware updated',
  firmware_confirmed: 'Firmware confirmed',
  firmware_update_failed: 'Firmware update failed',
  battery_low: 'Battery low alert',
  battery_critical: 'Battery critical alert',
  battery_ok: 'Battery back up',
  quiet: 'Gone quiet (24 h)',
  display_lost: 'Screen lost',
  display_shown: 'Screen redrawn',
  registered: 'Registered',
  retired: 'Retired',
  unretired: 'Back in service',
  pallet_outsourced: 'Pallet at vendor',
  pallet_returned: 'Pallet back',
  pallet_pulled: 'Beacon pulled (outsourced)',
  pallet_reattached: 'Beacon back on',
};

export function eventLabel(type) {
  return EVENT_LABELS[type] || String(type || '').replace(/_/g, ' ');
}

/** Tone for an event chip / marker: problems red, power green, the rest neutral. */
export function eventTone(type) {
  if (['find_failed', 'find_expired', 'battery_critical', 'display_lost', 'firmware_update_failed', 'quiet'].includes(type)) return 'danger';
  if (type === 'battery_low') return 'warning';
  if (['docked', 'battery_ok'].includes(type)) return 'success';
  return 'secondary';
}

/** Markers for the chart: the events the API marks as chart-worthy, newest last, within the points' span. */
export function chartMarkers(events, chartTypes, points) {
  if (!points || points.length === 0) return [];
  const lo = points[0].t;
  const hi = points[points.length - 1].t;
  const keep = new Set(chartTypes || []);
  return (events || [])
    .filter((e) => keep.has(e.event_type))
    .map((e) => ({ t: new Date(e.created_at).getTime(), type: e.event_type, label: eventLabel(e.event_type), tone: eventTone(e.event_type) }))
    .filter((m) => Number.isFinite(m.t) && m.t >= lo && m.t <= hi)
    .sort((a, b) => a.t - b.t);
}

/** One line on how long the battery lasts: from the API's drain estimate. */
export function drainText(d) {
  if (!d) return 'No estimate yet';
  if (d.status === 'draining') {
    const rate = `${Math.abs(d.pct_per_day).toFixed(1)}%/day`;
    if (!Number.isFinite(d.days_to_low)) return `Draining ${rate}`;
    if (d.days_to_low <= 0) return `Draining ${rate} · below ${LOW_PCT}% now`;
    return `~${formatDays(d.days_to_low)} to ${LOW_PCT}% (${rate})`;
  }
  if (d.status === 'steady') return 'No measurable drain yet';
  // jobq-api needs 3 days off the charger and a clear fall before it estimates (beacon_stats.drain_estimate)
  return d.samples > 0 ? `Learning: needs 3 days off the charger (${d.samples} reading${d.samples === 1 ? '' : 's'} so far)`
    : 'Learning: needs 3 days off the charger';
}

/** A short "days left" for the beacon list, or null when there's nothing worth saying. */
export function daysLeftShort(d) {
  if (!d || d.status !== 'draining' || !Number.isFinite(d.days_to_low)) return null;
  if (d.days_to_low <= 0) return { text: 'at 20%', tone: 'danger' };
  return { text: `~${formatDays(d.days_to_low)} left`, tone: d.days_to_low < 14 ? 'warning' : 'muted' };
}

export function formatDays(days) {
  if (!Number.isFinite(days)) return '—';
  if (days < 1) return `${Math.max(1, Math.round(days * 24))} h`;
  if (days < 60) return `${Math.round(days)} d`;
  if (days < 730) return `${Math.round(days / 30.4)} mo`;
  return `${(days / 365).toFixed(1)} yr`;
}

/** "45 min" / "3.2 h" / "4 d" between two ISO times (or to now). */
export function durationText(fromIso, toIso = null, now = Date.now()) {
  const a = new Date(fromIso).getTime();
  const b = toIso ? new Date(toIso).getTime() : now;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return '—';
  const m = Math.max(0, (b - a) / 60000);
  if (m < 60) return `${Math.round(m)} min`;
  if (m < 48 * 60) return `${(m / 60).toFixed(1)} h`;
  return `${Math.round(m / 1440)} d`;
}

export function secondsText(s) {
  return Number.isFinite(s) ? `${s < 10 ? s.toFixed(1) : Math.round(s)} s` : '—';
}

/** Axis tick: time of day for a 24 h range, else month/day. Shop time (Central). */
export function tickText(ms, days) {
  const d = new Date(ms);
  const opts = days <= 1
    ? { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' }
    : { month: 'numeric', day: 'numeric', timeZone: 'America/Chicago' };
  return d.toLocaleString('en-US', opts);
}

export function whenText(ms) {
  return new Date(ms).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago',
  });
}

/** The fleet strip's Find tile: "96% lit · 8.0 s median" or a quiet line. */
export function findTileText(f) {
  if (!f || !f.requested) return { main: '—', sub: 'No finds yet' };
  const main = Number.isFinite(f.success_pct) ? `${Math.round(f.success_pct)}%` : '—';
  const sub = `${f.lit} of ${f.requested} lit${Number.isFinite(f.median_s) ? ` · ${secondsText(f.median_s)} median` : ''}`;
  return { main, sub };
}

/** Per-gateway find rows sorted by tries that lit, with a success % over settled tries. */
export function gatewayFindRows(byGateway) {
  return (byGateway || [])
    .map((g) => {
      const settled = (g.ok || 0) + (g.failed || 0) + (g.lapsed || 0);
      return { ...g, settled, success_pct: settled ? Math.round((100 * (g.ok || 0)) / settled) : null };
    })
    .sort((a, b) => (b.ok || 0) - (a.ok || 0) || String(a.gateway_id).localeCompare(String(b.gateway_id)));
}
